'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';

const EclipseMap = dynamic(() => import('@/components/map/EclipseMap'), {
  ssr: false,
  loading: () => (
    <div
      className="flex-1 flex items-center justify-center"
      style={{ background: 'var(--pub-surface-2)' }}
    >
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          border: '2px solid var(--pub-border)',
          borderTopColor: 'var(--pub-green)',
          animation: 'pub-spin 1s linear infinite',
        }}
      />
    </div>
  ),
});

interface MapExperience {
  id: string;
  codeId: string;
  latitude: number;
  longitude: number;
  location?: string;
  message?: string;
  photoUrl?: string;
  scannedAt: string;
  isPublic: boolean;
}

interface MapModalProps {
  open: boolean;
  onClose: () => void;
}

export function MapModal({ open, onClose }: MapModalProps) {
  const t = useTranslations('public');
  const [experiences, setExperiences] = useState<MapExperience[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);

    fetch('/api/eclipse/map?limit=1000')
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setExperiences(data.experiences || []);
        setTotal(data.total || 0);
      })
      .catch(() => {
        // Silently fail — map just won't show pins
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col"
      style={{ background: 'var(--pub-surface)' }}
    >
      {/* Header */}
      <div
        className="flex justify-between items-center flex-shrink-0"
        style={{ padding: '52px 20px 12px' }}
      >
        <div>
          <h2
            style={{
              fontFamily: 'var(--font-pub-display)',
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'var(--pub-text-primary)',
            }}
          >
            {t('map.title')}
          </h2>
          <p
            style={{
              fontFamily: 'var(--font-pub-mono)',
              fontSize: 10,
              color: 'var(--pub-text-muted)',
              marginTop: 3,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {total > 0 ? `${total.toLocaleString()} ${t('map.subtitle')}` : t('map.subtitle')}
          </p>
        </div>
        <button
          onClick={onClose}
          className="flex items-center justify-center cursor-pointer transition-colors duration-200"
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            border: '1px solid var(--pub-border)',
            background: 'var(--pub-surface-2)',
            color: 'var(--pub-text-secondary)',
          }}
          aria-label={t('map.close')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Map — reuses existing EclipseMap with real scan data */}
      <div
        className="flex-1 relative overflow-hidden"
        style={{
          margin: '0 16px',
          borderRadius: 20,
          border: '1px solid var(--pub-border)',
        }}
      >
        {!loading ? (
          <EclipseMap experiences={experiences} className="w-full h-full" />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ background: 'var(--pub-surface-2)' }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: '2px solid var(--pub-border)',
                borderTopColor: 'var(--pub-green)',
                animation: 'pub-spin 1s linear infinite',
              }}
            />
          </div>
        )}

        {/* Verified count badge */}
        {total > 0 && (
          <div
            className="absolute bottom-3 right-3 z-10"
            style={{
              background: 'var(--pub-surface)',
              border: '1px solid var(--pub-border)',
              borderRadius: 100,
              padding: '4px 12px',
              fontFamily: 'var(--font-pub-mono)',
              fontSize: 10,
              color: 'var(--pub-green)',
              letterSpacing: '0.06em',
            }}
          >
            &#9679; {total.toLocaleString()} {t('map.badge')}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-col gap-2 flex-shrink-0" style={{ padding: '14px 16px 32px' }}>
        <button className="pub-btn pub-btn-secondary" onClick={onClose}>
          {t('map.close')}
        </button>
      </div>
    </div>
  );
}
